/* eslint-disable max-lines */
import { Bars3Icon, SparklesIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm
} from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { IAIContentResponseDto } from "../../../interfaces/endpoints/IAIContent";
import { ILinePageSectionEditDto } from "../../../interfaces/endpoints/ILinePage";
import { editLinePageSection, getLinePageSections } from "../../../services/LinePages.service";
import FormWrapper from "../../ui/FormWrapper";
import IconBtn from "../../ui/IconBtn";
import SpinnerLoader from "../../ui/SpinnerLoader";
import AIGenerateModal from "./AIGenerateModal";
import SectionCreator from "./SectionCreator";

const EditSections = () => {
  const { id } = useParams<{ id: string }>();
  const [showAIModal, setShowAIModal] = useState(false);

  const methods = useForm<{ sections: ILinePageSectionEditDto[] }>({
    defaultValues: { sections: [] }
  });

  const { control, handleSubmit, reset, watch } = methods;
  const { append, move } = useFieldArray({ control, name: "sections" });
  const dragItem = useRef<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragItem.current = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragItem.current === null || dragItem.current === index) return;
    move(dragItem.current, index);
    dragItem.current = index;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragItem.current = null;
  };
  const { response ,loading ,refetch } = useFetch(() => getLinePageSections(Number(id)), {
    initRequest: true
  });

  useEffect(() => {
    if (response?.data) {
      reset({ sections: response.data });
    }
  }, [response, reset]);

  const sections = watch("sections");

  const handleDeleteSection = (sectionId: number, index: number) => {
    const updatedSections =
      sectionId === 0
        ? sections.filter((_, i) => i !== index)
        : sections.filter((section) => section.id !== sectionId);
    reset({ sections: updatedSections });
  };

  const handleAIGenerated = (aiResponse: IAIContentResponseDto) => {
    aiResponse.sections.forEach((aiSection) => {
      append({
        id: 0,
        linePageId: Number(id),
        displayOrder: sections.length,
        locales: aiSection.locales.map((l) => ({
          languageId: l.languageId,
          title: l.title,
          content: l.content
        })),
        sectionPictureMapping: []
      });
    });
  };

  const onSubmit: SubmitHandler<{
    sections: ILinePageSectionEditDto[];
  }> = async (data) => {
    try {
      // Synchronize displayOrder with current array index
      const orderedSections = data.sections.map((section, index) => ({
        ...section,
        displayOrder: index
      }));

      await editLinePageSection(id ?? "", orderedSections);
      toast.success("Linja është përditësuar me sukses.", {
        position: "top-right"
      });
      refetch();
    } catch {
      toast.error("Ndodhi një gabim gjatë përditësimit.", {
        position: "top-right"
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        title="Modifiko Seksionet e Linjës"
        contentClassName="max-w-7xl mx-auto"
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
      >
        <div className="min-h-[600px] flex items-center justify-center">
          {loading ? (
            <SpinnerLoader className="text-primary" />
          ) : (
            <div className="w-full">

              <div className="w-full mb-6">
                {sections.map((section, index) => (
                  <div
                    key={section.id || index}
                    className="w-full flex flex-row items-start gap-2 group mb-4"
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={handleDrop}
                  >
                    <div className="mt-7 cursor-move text-gray-400 hover:text-primary transition-colors">
                      <Bars3Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <SectionCreator section={section} sectionIndex={index} />
                    </div>
                    <IconBtn
                      className="w-10 h-10 text-neutral select-none mt-5"
                      onClick={() => handleDeleteSection(section.id, index)}
                    >
                      <TrashIcon className="w-6 h-6" />
                    </IconBtn>
                  </div>
                ))}
              </div>
              <div className="flex flex-row justify-center items-center gap-4 mt-6">
                <button
                  type="button"
                  className={
                    "p-2 px-4 font-semibold bg-primary text-white " +
                    "rounded-xl shadow-sm hover:bg-opacity-90 transition-all"
                  }
                  onClick={() =>
                    append({
                      id: 0,
                      linePageId: Number(id),
                      displayOrder: sections.length,
                      locales: [
                        { languageId: 1, title: "", content: "" },
                        { languageId: 2, title: "", content: "" }
                      ],
                      sectionPictureMapping: []
                    })
                  }
                >
                  Shto Seksion
                </button>

                <button
                  type="button"
                  className={
                    "flex items-center gap-2 px-4 py-2 bg-primary text-white " +
                    "rounded-xl hover:bg-opacity-90 transition-all font-semibold shadow-sm"
                  }
                  onClick={() => setShowAIModal(true)}
                >
                  <SparklesIcon className="w-5 h-5 text-white" />
                  Gjenero me AI
                </button>
              </div>
            </div>
          )}
        </div>

        <AIGenerateModal
          show={showAIModal}
          onClose={() => setShowAIModal(false)}
          entityId={Number(id)}
          pageType={0} // LinePage
          existingSections={sections}
          onGenerated={handleAIGenerated}
        />
      </FormWrapper>
    </FormProvider>
  );
};

export default EditSections;
